import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getDiscussions, streamURL } from '../api/discussions';
import { useAuth } from './AuthContext';

// Une seule connexion SSE pour toute l'app (montee dans Layout).
// Les abonnes (fil ouvert) recoivent les evenements bruts via subscribe().
const Ctx = createContext({
  discussions: [],
  unreadTotal: 0,
  refresh: () => {},
  subscribe: () => () => {},
});

export function DiscussionsProvider({ children }) {
  const { user } = useAuth();
  const [discussions, setDiscussions] = useState([]);
  const listeners = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      const r = await getDiscussions();
      setDiscussions(r.data);
    } catch { /* hors-ligne ou 401 : on garde l'etat courant */ }
  }, []);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  // Dependre de user.id et non de l'objet user : AuthContext remplace l'objet
  // apres revalidation, ce qui fermait et rouvrait la connexion pour rien.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    refresh();
    const es = new EventSource(streamURL());
    // ponytail: chaque evenement refetch la liste entiere — simple et correct
    // a l'echelle d'une equipe. Mettre a jour localement si ca devient lourd.
    const relayer = (type) => (e) => {
      let data = {};
      try { data = JSON.parse(e.data); } catch { /* ignore */ }
      listeners.current.forEach((fn) => fn({ type, data }));
      refresh();
    };
    es.addEventListener('message_nouveau', relayer('message_nouveau'));
    es.addEventListener('message_supprime', relayer('message_supprime'));
    es.addEventListener('message_sav', relayer('message_sav'));
    es.addEventListener('message_modifie', relayer('message_modifie'));
    es.addEventListener('pouce', relayer('pouce'));
    es.addEventListener('conversation', relayer('conversation'));
    es.addEventListener('lu', relayer('lu'));
    // « Vu » et « en train d'ecrire » ne changent pas la liste : pas de refetch.
    const relayerSeul = (type) => (e) => {
      let data = {};
      try { data = JSON.parse(e.data); } catch { /* ignore */ }
      listeners.current.forEach((fn) => fn({ type, data }));
    };
    es.addEventListener('vu', relayerSeul('vu'));
    es.addEventListener('ecrit', relayerSeul('ecrit'));
    es.onopen = refresh; // rattrape ce qui a ete manque pendant une coupure
    return () => es.close();
  }, [userId, refresh]);

  const unreadTotal = discussions.reduce((n, d) => n + (d.non_lus || 0), 0);

  return (
    <Ctx.Provider value={{ discussions, unreadTotal, refresh, subscribe }}>
      {children}
    </Ctx.Provider>
  );
}

export const useDiscussions = () => useContext(Ctx);
